import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import { useChannel } from '../contexts/ChannelContext';

/**
 * Footer — "map of the universe" per channel.
 *
 * Each of the three channels (League / Dreamers / FLYT) gets its own footer
 * personality:
 *  - brand wordmark + tagline matches the channel
 *  - link columns route INTERNALLY (League stays on League, Dreamers stays
 *    on Dreamers, FLYT stays on FLYT)
 *  - cross-channel discovery only happens in the "Universe" column so each
 *    site still feels self-contained
 */

const LEAGUE_COLUMNS = [
  {
    title: 'Explore',
    links: [
      { to: '/stories', label: 'Stories' },
      { to: '/', label: 'The Network' },
      { to: '/athletes', label: 'Athletes' },
      { to: '/collections', label: 'Collections' },
    ],
  },
  {
    title: 'Access',
    links: [
      { to: '/profile', label: 'My Access' },
      { to: '/profile?tab=following', label: 'Following' },
      { to: '/profile?tab=stories', label: 'Saved' },
      { to: '/search', label: 'Search' },
    ],
  },
  {
    title: 'Partner',
    links: [
      { to: '/for-athletes', label: 'For Athletes' },
      // iter583 · "For Brands" removed from public footer per user
      // request — route + landing page kept intact for later restore.
      { to: '/for-programs', label: 'For Programs' },
      { to: '/school-partnerships', label: 'School Partnerships' },
    ],
  },
  {
    title: 'About',
    links: [
      { to: '/contact', label: 'Contact' },
      { to: '/privacy', label: 'Privacy' },
    ],
  },
];

const VISIONARY_COLUMNS = [
  {
    title: 'Explore',
    links: [
      { to: '/visionary/stories', label: 'Dreamers Stories' },
      { to: '/visionary/network', label: 'The Dreamers Network' },
      { to: '/visionary/voices', label: 'Visionaries' },
    ],
  },
  {
    title: 'Access',
    links: [
      { to: '/visionary/profile', label: 'My Access' },
      { to: '/visionary/profile?tab=following', label: 'Following' },
      { to: '/visionary/profile?tab=stories', label: 'Saved' },
      { to: '/search', label: 'Search' },
    ],
  },
  {
    title: 'Tell Your Story',
    links: [
      { to: '/visionary/contact?type=Artist', label: 'For Artists' },
      { to: '/visionary/contact?type=CEO', label: 'For CEOs' },
      { to: '/visionary/contact?type=Entrepreneur', label: 'For Entrepreneurs' },
      { to: '/visionary/contact?type=Pioneer', label: 'For Pioneers' },
    ],
  },
  {
    title: 'About',
    links: [
      { to: '/visionary/about', label: 'About Jay' },
      { to: '/visionary/contact', label: 'Contact' },
      { to: '/privacy', label: 'Privacy' },
    ],
  },
];

const FLYT_COLUMNS = [
  {
    title: 'Studios',
    links: [
      { to: '/flyt/process', label: 'Our Process' },
      { to: '/flyt/case-studies', label: 'FLYT Built Stories' },
      { to: '/flyt/stories', label: 'Stories' },
    ],
  },
  {
    title: 'Work With Us',
    links: [
      // iter583 · "For Brands" hidden on Jay's request — route kept.
      { to: '/flyt/contact', label: 'Pitch a Story' },
      { to: '/flyt/contact?type=Sponsorship', label: 'Sponsorship' },
    ],
  },
  {
    title: 'Access',
    links: [
      { to: '/flyt/profile', label: 'My Profile' },
      { to: '/search', label: 'Search' },
    ],
  },
  {
    title: 'About',
    links: [
      { to: '/flyt/about', label: 'About FLYT' },
      { to: '/flyt/contact', label: 'Contact' },
      { to: '/privacy', label: 'Privacy' },
    ],
  },
];

const IG_URL = 'https://www.instagram.com/jaypernell_?igsh=NHo5NDQ0bzZyc2M%3D&utm_source=qr';
const SOCIAL_BY_CHANNEL = {
  dreams_league: [
    { href: IG_URL, label: 'Instagram', Icon: Instagram },
  ],
  dreams_visionary: [
    { href: IG_URL, label: 'Instagram', Icon: Instagram },
  ],
  flyt: [
    { href: IG_URL, label: 'Instagram', Icon: Instagram },
  ],
};

const COPY_BY_CHANNEL = {
  dreams_league: {
    wordmarkPrefix: 'DREAMS',
    wordmarkAccent: 'LEAGUE',
    accentClass: 'blue-gradient',
    tagline: 'The home for extraordinary journeys. Step inside the worlds of elite athletes through exclusive documentaries, authentic conversations, and behind-the-scenes access.',
    columnAccent: 'text-blue-300',
    quote: "I'm the guy athletes trust to tell their real story.",
  },
  dreams_visionary: {
    wordmarkPrefix: 'DREAMS',
    wordmarkAccent: 'DREAMERS',
    accentClass: 'text-white',
    tagline: 'Artists. CEOs. Entrepreneurs. Pioneers. The unfiltered build, told without the PR team in the room.',
    columnAccent: 'text-gray-300',
    quote: 'Every voice. Every vision. One platform.',
  },
  flyt: {
    wordmarkPrefix: 'FLYT',
    wordmarkAccent: 'STUDIOS',
    accentClass: 'gold-gradient',
    tagline: 'The studio that creates the Dreams League stories — built for brands, athletes, and the network behind every drop.',
    columnAccent: 'text-yellow-300',
    quote: 'The studio behind the Dreams League stories.',
  },
};

const UNIVERSE = [
  { to: '/', label: 'FLYT League', channelId: 'dreams_league' },
  // Dreamers channel is hidden from the universe rail for now — it disrupts
  // the current flow. Restore by un-commenting when the Dreamers experience
  // is back in market.
  // { to: '/visionary', label: 'Dreamers', channelId: 'dreams_visionary' },
  // iter516j · FLYT Studios entry hidden while primary focus is
  // athlete onboarding. Keep the route/page code intact — just
  // remove the footer link so athletes/agents/brands don't get
  // distracted. Re-enable by un-commenting the line below when
  // Studios is ready to promote again.
  // { to: '/flyt', label: 'FLYT Studios', channelId: 'flyt' },
];

const Footer = () => {
  const channel = useChannel();
  // iter571 · Suppress the entire marketing footer on the native iOS
  // Capacitor shell. Web keeps the full footer for SEO + discovery;
  // the app doesn't need marketing links since the same routes are
  // reachable via the bottom nav and side sheet — and Apple prefers
  // no external web nav in the shell.
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  const isNativeIOS = !!cap?.isNativePlatform?.() && cap?.getPlatform?.() === 'ios';
  if (isNativeIOS) return null;

  const cols =
    channel.id === 'flyt' ? FLYT_COLUMNS
    : channel.id === 'dreams_visionary' ? VISIONARY_COLUMNS
    : LEAGUE_COLUMNS;
  const copy = COPY_BY_CHANNEL[channel.id] || COPY_BY_CHANNEL.dreams_league;
  const social = SOCIAL_BY_CHANNEL[channel.id] || SOCIAL_BY_CHANNEL.dreams_league;

  return (
    <footer className="bg-black text-white pt-8 sm:pt-16 pb-6 sm:pb-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Brand — wordmark + tagline removed per founder request (iter284) */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              {social.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-black transition flex items-center justify-center"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* 4 link columns — 2×2 grid on mobile, 4-across on desktop */}
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-5 md:gap-6">
            {cols.map((col) => (
              <div key={col.title}>
                <h4 className={`font-display text-[10px] sm:text-[11px] mb-2 sm:mb-4 uppercase tracking-[0.22em] ${copy.columnAccent}`}>
                  {col.title}
                </h4>
                <ul className="space-y-1.5 sm:space-y-2.5 text-[13px] sm:text-sm">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className={`hover-gradient transition ${l.emphasize ? 'font-bold text-white' : 'text-gray-300'}`}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Universe switcher — only place where cross-channel links live */}
        <div className="mt-8 sm:mt-12 pt-5 sm:pt-8 border-t border-white/10">
          <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-2 sm:mb-3">The Universe</p>
          <div className="flex flex-wrap gap-x-5 sm:gap-x-6 gap-y-2 text-[13px] sm:text-sm">
            {UNIVERSE.map((u) => {
              const isCurrent = u.channelId === channel.id;
              return (
                <Link
                  key={u.to}
                  to={u.to}
                  className={`font-display uppercase tracking-wider text-[12px] sm:text-sm transition ${
                    isCurrent ? 'text-white' : 'text-gray-500 hover:text-white'
                  }`}
                  data-testid={`footer-universe-${u.channelId}`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {u.label}
                  {isCurrent && <span className="ml-1.5 sm:ml-2 text-[10px] align-middle text-blue-400">●</span>}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-500">
          <p>© 2026 FLYT House™. All rights reserved.</p>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/terms"
              className="text-gray-400 hover:text-white transition"
              data-testid="footer-link-terms"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-white transition"
              data-testid="footer-link-privacy"
            >
              Privacy Policy
            </Link>
            <p className="font-italic hidden sm:block">&quot;{copy.quote}&quot; — Jay Pernell</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
